import { useMemo, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { RotateCcw, Truck } from 'lucide-react'
import { Button, SearchField, Toolbar, Tooltip } from '@/components/ds2'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import { BOX_TRUCK_MAP_SEARCH_RESULT_LIMIT } from '../constants/boxTruckMap'
import { findMatchingDrivers, getDriverSearchLabel } from '../utils/boxTruckMapState'

interface BoxTruckMapToolbarProps {
    drivers: BoxTruckMapDriver[]
    onSelectDriver: (driver: BoxTruckMapDriver) => void
    onReset: () => void
}

export const BoxTruckMapToolbar = ({ drivers, onSelectDriver, onReset }: BoxTruckMapToolbarProps) => {
    const [query, setQuery] = useState('')
    const [resultsOpen, setResultsOpen] = useState(false)
    const matches = useMemo(() => findMatchingDrivers(drivers, query, BOX_TRUCK_MAP_SEARCH_RESULT_LIMIT), [drivers, query])

    const selectDriver = (driver: BoxTruckMapDriver) => {
        onSelectDriver(driver)
        setQuery('')
        setResultsOpen(false)
    }

    return (
        <div className="pointer-events-none absolute left-4 right-14 top-4 z-10 flex flex-wrap items-start gap-2">
            <Popover.Root open={resultsOpen && Boolean(query.trim())} onOpenChange={setResultsOpen}>
                <Popover.Anchor asChild>
                    <div className="pointer-events-auto w-[min(280px,100%)]">
                        <SearchField
                            value={query}
                            placeholder="Find driver or truck #"
                            showLabel={false}
                            showDescription={false}
                            onChange={(value) => {
                                setQuery(value)
                                setResultsOpen(Boolean(value.trim()))
                            }}
                            onClear={() => setResultsOpen(false)}
                            onSubmit={() => {
                                const firstMatch = matches[0]
                                if (firstMatch) selectDriver(firstMatch)
                            }}
                        />
                    </div>
                </Popover.Anchor>
                <Popover.Portal>
                    <Popover.Content
                        align="start"
                        sideOffset={8}
                        collisionPadding={8}
                        onOpenAutoFocus={(event) => event.preventDefault()}
                        className="z-[1200] w-[280px] rounded-2xl bg-surface p-2 shadow-overlay"
                    >
                        {matches.length > 0 ? (
                            <>
                                <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                                    {matches.length === BOX_TRUCK_MAP_SEARCH_RESULT_LIMIT
                                        ? `Showing first ${BOX_TRUCK_MAP_SEARCH_RESULT_LIMIT} matching drivers`
                                        : `${matches.length} matching ${matches.length === 1 ? 'driver' : 'drivers'}`}
                                </p>
                                <div className="flex flex-col gap-0.5" role="listbox" aria-label="Matching drivers">
                                    {matches.map((driver) => (
                                        <button
                                            key={driver.driverId}
                                            type="button"
                                            role="option"
                                            aria-selected={false}
                                            onClick={() => selectDriver(driver)}
                                            className="flex min-h-10 w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        >
                                            <span
                                                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${driver.verified ? 'bg-success-soft text-success-soft-foreground' : 'bg-warning-soft text-warning-soft-foreground'}`}
                                            >
                                                <Truck size={14} aria-hidden />
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block truncate text-sm font-medium text-foreground">{getDriverSearchLabel(driver)}</span>
                                                <span className="block truncate text-xs text-foreground-muted">
                                                    {[driver.city, driver.state].filter(Boolean).join(', ') || 'Location unavailable'}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="px-2 py-1.5 text-sm text-foreground-muted">No drivers found.</p>
                        )}
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>

            <div className="pointer-events-auto">
                <Toolbar>
                    <Tooltip content="Reset map view" placement="bottom">
                        <Button variant="ghost" size="sm" iconOnly aria-label="Reset map view" onClick={onReset}>
                            <RotateCcw size={14} aria-hidden />
                        </Button>
                    </Tooltip>
                </Toolbar>
            </div>
        </div>
    )
}
